import React, { useState, useEffect } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import FinlyLogo from "@/components/FinlyLogo";
import FinlyLogoMark from "@/components/FinlyLogoMark";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("session") === "expired") {
      toast.info("Tu sesión expiró. Inicia sesión de nuevo.");
    }
  }, [searchParams]);

  if (user && typeof user === "object") return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("¡Bienvenido de vuelta!");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Error al iniciar sesión");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Form side */}
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12 bg-[#0B1220]">
        <div className="max-w-md w-full mx-auto">
          <div className="flex flex-col items-start gap-4 mb-12">
            <FinlyLogoMark size={120} />
            <span
              className="finly-tagline-animated inline-flex items-center gap-2 text-[18px] font-medium text-[#8A9A5B]"
              data-testid="login-tagline"
            >
              Tu dinero, en orden
              <svg
                className="finly-tagline-check w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#8A9A5B"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12l4.5 4.5L19 7" />
              </svg>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#F1F5F9] mb-3">
            Bienvenido
          </h1>
          <p className="text-[#94A3B8] mb-10 text-base leading-relaxed">
            Inicia sesión para ver a dónde se fue tu dinero este mes.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="tu@correo.com"
                className="mt-2 h-12 bg-[#131C2E] border-[#2A3447] text-[#E2E8F0] placeholder:text-[#64748B] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF] rounded-md"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-2 h-12 bg-[#131C2E] border-[#2A3447] text-[#E2E8F0] placeholder:text-[#64748B] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF] rounded-md"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium rounded-md text-base"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Iniciar sesión"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-[#94A3B8]">
            ¿No tienes cuenta?{" "}
            <Link to="/register" className="text-[#0052FF] font-medium hover:underline" data-testid="link-register">
              Crear cuenta
            </Link>
          </p>

        </div>
      </div>

      {/* Hero side — Finly animated gradient (Trust Blue → Neon Green) */}
      <div className="hidden lg:block relative overflow-hidden finly-hero-gradient finly-hero-grid" data-testid="login-hero">
        <div className="finly-hero-orb" style={{ top: "10%", right: "-60px" }}></div>
        <div className="finly-hero-orb" style={{ bottom: "15%", left: "-80px", animationDelay: "4s" }}></div>
        <div className="absolute top-12 left-12 right-12 text-white/90 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-[#00E590] animate-pulse"></span>
          <span className="text-[11px] uppercase tracking-[0.35em] font-medium">Finly+ · Finanzas personales</span>
        </div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-display text-5xl xl:text-6xl font-black leading-[0.95] tracking-tight max-w-md">
            Tu dinero,<br/>
            <span className="text-[#00E590]">en orden.</span>
          </h2>
          <p className="mt-5 text-sm text-white/70 max-w-sm leading-relaxed">
            Sabe a dónde se fue cada peso este mes. Sin hojas de cálculo, sin estrés.
          </p>
        </div>
      </div>
    </div>
  );
}
